/**
 * Activities nested under activities — the place you were, and what you did there.
 *
 * A segment is a stretch of the trip ("park week", "the cruise"). That is the
 * right grain for planning, and the wrong one for logging: you check into the
 * park, and then everything for the next nine hours happened *there*. Before
 * this, all nine hours landed as one flat list beside the check-in that framed
 * them, and the only way to group them was to promote the park to a segment —
 * which is a thing you set up in advance, not a thing you tap once on the way
 * through a turnstile.
 *
 * So an activity can carry `parentInteractionId`: the airport holds the lounge
 * and the restaurant, the hotel holds its bar, the park holds the rides. It is
 * the same shape segments already have, one level down, and it nests as deep as
 * the day actually did.
 *
 * Two invariants keep this from splitting the data model in half:
 *
 *   1. A child carries its parent's `segmentId`. Every "activities of this
 *      segment" filter in the app still sees the whole tree; nesting changes
 *      how the list is *drawn*, never which segment an activity counts under.
 *      `descendantActivityIds` is how a move cascades that.
 *   2. A child whose parent isn't in the list being rendered is drawn as a
 *      root. Filters hide rows, and a hidden parent must not take its children
 *      off screen with it.
 */

import type { EntityType, InteractionEvent } from "../types";
import {
  activityEnd,
  activityStart,
  isActivityActive,
  isActivityOpen,
  type SegmentDayLookup,
} from "./tripDisplay";

export interface ActivityNodeLike {
  id: string;
  parentInteractionId?: string | null;
}

/** Direct children of each activity, keyed by parent id, in input order. */
export function activityChildrenMap<T extends ActivityNodeLike>(
  activities: T[],
): Map<string, T[]> {
  const byParent = new Map<string, T[]>();
  for (const a of activities) {
    const parentId = a.parentInteractionId;
    if (!parentId) continue;
    const arr = byParent.get(parentId);
    if (arr) arr.push(a);
    else byParent.set(parentId, [a]);
  }
  return byParent;
}

export interface ActivityForest<T> {
  /** Activities drawn at the top of the list. */
  roots: T[];
  /** parentId → its children, for the rows that have any. */
  childrenOf: Map<string, T[]>;
}

/**
 * Split a flat list into roots and children.
 *
 * An activity is a root when it names no parent, or when the parent it names
 * isn't in this list — a filtered view, a segment block that only holds part of
 * the tree, or a parent someone deleted out from under it. Orphans surface
 * rather than disappear.
 */
export function buildActivityForest<T extends ActivityNodeLike>(
  activities: T[],
): ActivityForest<T> {
  const present = new Set(activities.map((a) => a.id));
  const childrenOf = activityChildrenMap(activities);
  const roots = activities.filter(
    (a) => !a.parentInteractionId || !present.has(a.parentInteractionId),
  );
  // Only keep child lists that hang off a row actually being drawn, so a
  // caller can trust `childrenOf.get(id)` without re-checking presence.
  for (const parentId of [...childrenOf.keys()]) {
    if (!present.has(parentId)) childrenOf.delete(parentId);
  }
  return { roots, childrenOf };
}

/**
 * An activity's ancestors, nearest first — the chain of places it sat inside.
 * Stops at the first repeat, so a cycle written by a bad edit can't hang a render.
 */
export function activityAncestors<T extends ActivityNodeLike>(
  activities: T[],
  activityId: string,
): T[] {
  const byId = new Map(activities.map((a) => [a.id, a] as const));
  const chain: T[] = [];
  const seen = new Set<string>([activityId]);
  let parentId = byId.get(activityId)?.parentInteractionId ?? null;
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) break;
    chain.push(parent);
    parentId = parent.parentInteractionId ?? null;
  }
  return chain;
}

/**
 * Every id beneath an activity, excluding the activity itself. What a move has
 * to carry with it (invariant 1) and what a delete has to account for.
 */
export function descendantActivityIds<T extends ActivityNodeLike>(
  activities: T[],
  activityId: string,
): string[] {
  const childrenOf = activityChildrenMap(activities);
  const out: string[] = [];
  const seen = new Set<string>([activityId]);
  const stack = [activityId];
  while (stack.length > 0) {
    for (const child of childrenOf.get(stack.pop()!) ?? []) {
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      out.push(child.id);
      stack.push(child.id);
    }
  }
  return out;
}

export interface ActivityOptionRow {
  id: string;
  label: string;
  depth: number;
}

/**
 * A flat, indented list of activities for a "logged under" picker — the same
 * shape `buildNestedSegmentOptions` gives segments.
 *
 * `excludeSubtreeOf` drops an activity and everything under it, so nothing can
 * be filed inside its own descendant.
 */
export function buildNestedActivityOptions<T extends ActivityNodeLike>(
  activities: T[],
  labelOf: (activity: T) => string,
  options: { excludeSubtreeOf?: string | null } = {},
): ActivityOptionRow[] {
  const excluded = new Set<string>();
  if (options.excludeSubtreeOf) {
    excluded.add(options.excludeSubtreeOf);
    for (const id of descendantActivityIds(activities, options.excludeSubtreeOf)) {
      excluded.add(id);
    }
  }
  const visible = activities.filter((a) => !excluded.has(a.id));
  const { roots, childrenOf } = buildActivityForest(visible);

  const rows: ActivityOptionRow[] = [];
  const walk = (activity: T, depth: number) => {
    const indent = depth > 0 ? "\u00A0\u00A0".repeat(depth) + "└ " : "";
    rows.push({ id: activity.id, label: `${indent}${labelOf(activity)}`, depth });
    for (const child of childrenOf.get(activity.id) ?? []) walk(child, depth + 1);
  };
  for (const root of roots) walk(root, 0);
  return rows;
}

/**
 * Types that read as somewhere you *are* rather than something you did — a
 * park, a hotel, a ship, the airport you're stuck in. Only used to guess a
 * default parent; anything at all can be picked as one by hand, because a
 * dinner that ran a trivia round holds the trivia just fine.
 */
export const PLACE_LIKE_ENTITY_TYPES: ReadonlySet<EntityType> = new Set<EntityType>([
  "DESTINATION",
  "PARK",
  "EVENT",
  "HOTEL",
  "SHIP",
  "AIRPORT",
  "LOCATION",
]);

export interface ParentCandidateLike extends ActivityNodeLike {
  segmentId?: string | null;
  events?: InteractionEvent[];
  createdAt?: { toDate?: () => Date } | null;
}

/** A day either side of now — how long a place stays on offer as somewhere you
 *  might still be standing. */
const CANDIDATE_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * The activities worth offering as "log this under…", most recent first.
 *
 * Every activity in a segment is a *legal* parent, but a list of every ride of
 * a week-long trip is no use to someone standing in a queue. What's useful is
 * where you might still be: places in this segment that nobody has closed out
 * and that were logged in the last day — plus anything already holding
 * activities, since a place you've been filing under stays the place you're
 * filing under however long the stay runs.
 *
 * Filing under something older than that is done from the place's own page
 * ("log an activity here") or the activity's edit form, where the whole
 * segment is listed.
 */
export function parentCandidateActivities<T extends ParentCandidateLike>(
  activities: T[],
  segmentId: string | null,
  onDay?: SegmentDayLookup,
  nowMs: number = Date.now(),
): T[] {
  const childrenOf = activityChildrenMap(activities);
  const recencyMs = (a: T): number =>
    Math.max(
      activityEnd(a, onDay)?.getTime() ?? -Infinity,
      a.createdAt?.toDate?.()?.getTime() ?? -Infinity,
    );

  return activities
    .filter((a) => {
      if ((a.segmentId ?? null) !== segmentId) return false;
      if (childrenOf.has(a.id)) return true;
      return isActivityOpen(a.events) && nowMs - recencyMs(a) < CANDIDATE_WINDOW_MS;
    })
    .sort((a, b) => recencyMs(b) - recencyMs(a));
}

/**
 * Guess what a newly logged activity belongs under.
 *
 * The rule is deliberately narrow: the place has to be one you're still inside
 * (its last event isn't an END, and it was logged in the last 12 hours — the
 * same "Live" test the itinerary shows a pulse for), it has to be in the
 * segment being logged under, and it has to read as a place. Check into the
 * park and the next nine hours file themselves under it; log the park's
 * departure and they stop. Anything that already holds activities counts as a
 * place whatever its type, since it has demonstrably been used as one.
 *
 * A guess, never a decision: the log sheet shows it as a selected option the
 * user can change or clear before anything is written.
 */
export function findDefaultParentInteractionId<T extends ParentCandidateLike>(
  activities: T[],
  segmentId: string | null,
  typeOf: (activity: T) => EntityType | undefined,
  onDay?: SegmentDayLookup,
): string | null {
  const childrenOf = activityChildrenMap(activities);
  const candidates = parentCandidateActivities(activities, segmentId, onDay).filter((a) => {
    const type = typeOf(a);
    if (!isActivityActive(a.events, type)) return false;
    return (type != null && PLACE_LIKE_ENTITY_TYPES.has(type)) || childrenOf.has(a.id);
  });
  if (candidates.length === 0) return null;

  // The most recently started one — which, when you walk into a park and then
  // into a restaurant inside it, is the deepest place you're standing in.
  let best: T | null = null;
  let bestMs = -Infinity;
  for (const candidate of candidates) {
    const ms = activityStart(candidate, onDay)?.getTime() ?? -Infinity;
    if (ms >= bestMs) {
      bestMs = ms;
      best = candidate;
    }
  }
  return best?.id ?? null;
}
